/**
 * Lift-type parameter virtual views: de-prefixed, ancestry-enriched surfaces
 * fronting the `LiftTypeParameter_String` data-model view directly.
 *
 * The artificial-lift agent limit views (`EspLimits` / `GasLiftLimits`) omit
 * several lift-type operating parameters — the ESP/pump-speed motor-speed
 * limits, the generated power-available ceiling, and the un-prefixed rate /
 * pressure caps. This family reads the lift-type parameter view at its source,
 * so the agent can query the full operating-limit set per well string.
 *
 * ESP limits and gas-lift limits are mutually exclusive by a string's lift
 * type, so the surface is split into three purpose-scoped views — ESP, gas-lift,
 * and lift-type-independent rate/pressure caps — each carrying only its own
 * limits and matching active-monitoring flags rather than one combined view
 * forcing every consumer to carry half-null columns.
 *
 * The source carries per-string grain (keyed by the `wellString` producing-
 * string relation) and NO scenario prefix, so every group resolves with no
 * prefix (`prefix:''`, the default): the shape `suffix` is the verbatim source
 * column while the agent-facing `canonical` drops the `esp`/`gl` marker for
 * readability. Each view keeps identity + the wellString relation
 * (`STRING_KEEP`) and appends the well / field / reservoir ancestry resolved
 * through the FlatHierarchy / Entity registries (`STRING_CONTEXT_ENRICH`).
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import type { EnrichedViewSpec } from '../createEnrichedView.ts';
import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import {
  IDENTITY,
  LIFT_TYPE_ESP_ACTIVE_FLAGS,
  LIFT_TYPE_ESP_LIMITS,
  LIFT_TYPE_GAS_LIFT_ACTIVE_FLAGS,
  LIFT_TYPE_GAS_LIFT_LIMITS,
  LIFT_TYPE_RATE_ACTIVE_FLAGS,
  LIFT_TYPE_RATE_LIMITS,
  type Shape,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import { LiftTypeParameterStringView } from '#schema/generated/ipso/views.ts';

/**
 * Identity + the producing-string relation for `LiftTypeParameter_String`. The
 * source carries the producing string as `entityName` (a StringAsset), so it is
 * renamed to the canonical `wellString` every per-string view exposes, letting
 * the StringContext enrichment resolve ancestry off the same `wellString` ref.
 */
const LIFT_TYPE_STRING_KEEP: Shape = [
  ...IDENTITY,
  {
    canonical: 'wellString',
    suffix: 'entityName',
    description: 'Producing string (completion) this row belongs to.',
    kind: 'unitless',
  },
];

/** The per-view subset of {@link EnrichedViewSpec} that varies across the three lift-type parameter subviews. */
type LiftTypeParameterSpec = Pick<EnrichedViewSpec, 'name' | 'description' | 'groups'>;
/**
 * The three purpose-scoped lift-type parameter subviews, declared as data. ESP
 * and gas-lift limits are mutually exclusive by a string's lift type, so each
 * lift type gets its own view carrying only its limits and matching
 * active-monitoring flags; the lift-type-independent rate/pressure caps get their
 * own. All share the same source, kept columns and StringContext enrichment.
 */
const LIFT_TYPE_PARAMETER_SPECS: readonly LiftTypeParameterSpec[] = [
  {
    name: 'EspParameters',
    description: `ESP operating limits per well string from LiftTypeParameter_String: the ESP fluid-rate operating window, pump-speed limits per motor type, generated power-available ceiling, plus the per-parameter active-monitoring flags — parameters the artificial-lift agent ESP limit view omits${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: LIFT_TYPE_ESP_LIMITS }, { shape: LIFT_TYPE_ESP_ACTIVE_FLAGS }],
  },
  {
    name: 'GasLiftParameters',
    description: `Gas-lift operating limits per well string from LiftTypeParameter_String: the casing-head and manifold pressure ceilings and the minimum bottom-hole pressure target, plus the per-parameter active-monitoring flags — parameters the artificial-lift agent gas-lift limit view omits${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: LIFT_TYPE_GAS_LIFT_LIMITS }, { shape: LIFT_TYPE_GAS_LIFT_ACTIVE_FLAGS }],
  },
  {
    name: 'RateLimits',
    description: `Lift-type-independent rate and pressure caps per well string from LiftTypeParameter_String: the wellhead pressure ceiling and the liquid/oil rate and GOR caps, plus the per-parameter active-monitoring flags${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: LIFT_TYPE_RATE_LIMITS }, { shape: LIFT_TYPE_RATE_ACTIVE_FLAGS }],
  },
];

/**
 * Build the de-prefixed, ancestry-enriched lift-type parameter surface as three
 * purpose-scoped StringContext views over `LiftTypeParameter_String` + the
 * StringContext enrichment. ESP and gas-lift limits are mutually exclusive by a
 * string's lift type, so each lift type gets its own view carrying only its
 * limits and matching active-monitoring flags; the lift-type-independent
 * rate/pressure caps get their own. The source carries the producing string as
 * `entityName`, so the family keeps `LIFT_TYPE_STRING_KEEP` (renaming it to the
 * canonical `wellString`) instead of the default `STRING_KEEP`. Filter by
 * wellString (or join WellEntity) to scope by hierarchy.
 */
export const useLiftTypeParameterViews = makeStringContextViews({
  source: () => LiftTypeParameterStringView,
  keep: LIFT_TYPE_STRING_KEEP,
  specs: LIFT_TYPE_PARAMETER_SPECS,
});
